import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { getAuthHeaders, saveSession, clearSession } from "./lib/auth";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import DashboardPage from "./pages/DashboardPage";
import ResultsPage from "./pages/ResultsPage";
import PricingPage from "./pages/PricingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import DemoPage from "./pages/DemoPage";
import HomeGroupGuidePage from "./pages/HomeGroupGuidePage";
import ContentGeneratorPage from "./pages/ContentGeneratorPage";
import DevotionalGeneratorPage from "./pages/DevotionalGeneratorPage";
import SocialPostsGeneratorPage from "./pages/SocialPostsGeneratorPage";
import EbookGeneratorPage from "./pages/EbookGeneratorPage";
import AdminPage from "./pages/AdminPage";
import URLSummaryPage from "./pages/URLSummaryPage";
import CreatePage from "./pages/CreatePage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import StartTrialPage from "./pages/StartTrialPage";

// Components
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Root: authenticated → /dashboard, unauthenticated → the marketing home page
const RootRedirect = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { ...getAuthHeaders() }
        });
        setIsAuthenticated(response.ok);
      } catch (_) {
        setIsAuthenticated(false);
      }
    };
    check();
  }, []);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <HomePage />;
};

// Auth callback component for OAuth
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const location = useLocation();
  
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processSession = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: 'GET',
            headers: {
              'X-Session-ID': sessionId,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            saveSession(userData);
            window.location.href = userData.subscription_tier === 'trial_pending'
              ? '/start-trial'
              : '/dashboard';
          } else {
            window.location.href = '/login?error=auth_failed';
          }
        } catch (error) {
          console.error('Auth error:', error);
          window.location.href = '/login?error=auth_failed';
        }
      }
    };
    
    processSession();
  }, [location]);
  
  return (
    <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-stone-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const location = useLocation();
  
  useEffect(() => {
    const checkAuth = async () => {
      // Check if user data passed from location state
      if (location.state?.user) {
        setUser(location.state.user);
        setIsAuthenticated(true);
        return;
      }
      
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { ...getAuthHeaders() }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem('user', JSON.stringify(userData));
        } else {
          setIsAuthenticated(false);
          clearSession();
        }
      } catch (error) {
        setIsAuthenticated(false);
        clearSession();
      }
    };
    
    checkAuth();
  }, [location.state]);
  
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // New signups must finish the card-required trial checkout before reaching
  // protected pages. Skip this gate while a Stripe session_id is present in the
  // URL (landing back from Checkout's success_url) or while already on
  // /start-trial — otherwise this redirect races the post-checkout confirm call
  // and can fire a second checkout session for a trial that just started.
  const hasCheckoutSessionId = new URLSearchParams(location.search).has('session_id');
  if (
    user?.subscription_tier === 'trial_pending' &&
    location.pathname !== '/start-trial' &&
    !hasCheckoutSessionId
  ) {
    return <Navigate to="/start-trial" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check for OAuth callback (session_id in hash)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route
        path="/start-trial"
        element={
          <ProtectedRoute>
            <StartTrialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/results/:analysisId" 
        element={
          <ProtectedRoute>
            <ResultsPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/guide"
        element={
          <ProtectedRoute>
            <HomeGroupGuidePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/content"
        element={
          <ProtectedRoute>
            <ContentGeneratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devotional"
        element={
          <ProtectedRoute>
            <DevotionalGeneratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            <SocialPostsGeneratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ebook"
        element={
          <ProtectedRoute>
            <EbookGeneratorPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route
        path="/url-summary"
        element={
          <ProtectedRoute>
            <URLSummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create/:analysisId"
        element={
          <ProtectedRoute>
            <CreatePage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/signup" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
